import { cloneElement, useEffect, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import MuiTooltip from "@mui/material/Tooltip";
import { combineClasses } from "@/lib/utils";
import TooltipErrorBoundary from "./TooltipErrorBoundary";
import styles from "./Tooltip.module.scss";

// A styled wrapper around MUI's own `Tooltip` — positioning (flip/clamp
// to the viewport), the portal, show/hide delays, and keyboard/hover
// accessibility wiring all come from MUI's Popper-based implementation
// rather than being reimplemented here. What's left genuinely custom:
// `onlyIfTruncated` (MUI has no notion of "only show when the trigger's
// text overflows" — the common table-cell case) and a small error
// boundary around caller-supplied content.
export type TooltipPlacement = "top" | "bottom" | "left" | "right";
export type TooltipTheme = "dark" | "light";

export interface TooltipProps {
  /** The tooltip's own content — arbitrary JSX, not just a string. */
  content: ReactNode;
  /**
   * The trigger element. Must be able to hold a ref (any DOM element
   * works; a custom component needs to forward one) — MUI's own docs
   * call this out for the same reason: a plain string/fragment child
   * can't receive the ref this component (and MUI internally) attaches.
   * Wrap plain text in a `<span>` if that's all you have.
   */
  children: ReactElement;
  placement?: TooltipPlacement;
  theme?: TooltipTheme;
  /**
   * Only shows the tooltip when the trigger's own content is actually
   * overflowing (`scrollWidth > clientWidth` for a single-line trigger,
   * `scrollHeight > clientHeight` for a multi-line one) — tracked live via
   * `ResizeObserver` (a column narrowing) and re-checked when `children`
   * changes (new text overflowing a fixed-width cell, which doesn't fire
   * a resize event on its own).
   */
  onlyIfTruncated?: boolean;
  /**
   * Lets the pointer move from the trigger onto the tooltip itself
   * without it closing — needed when `content` has its own interactive
   * element (e.g. a copy button). Maps to MUI's `disableInteractive`
   * (inverted): non-interactive tooltips also ignore pointer events so
   * they never block a click on whatever they're covering.
   */
  interactive?: boolean;
  maxWidth?: number | string;
  /**
   * Maximum height of the tooltip content before it becomes scrollable.
   * Defaults to 180px for a clean, compact tooltip.
   */
  maxHeight?: number | string;
  disabled?: boolean;
  arrow?: boolean;
  className?: string | undefined;
}

const isTruncated = (el: HTMLElement) => el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;

const Tooltip = ({
  content,
  children,
  placement = "top",
  theme = "dark",
  onlyIfTruncated = false,
  interactive = false,
  maxWidth = 320,
  maxHeight = 180,
  disabled = false,
  arrow = true,
  className,
}: TooltipProps) => {
  const triggerRef = useRef<HTMLElement | null>(null);
  const [isContentTruncated, setIsContentTruncated] = useState(false);

  useEffect(() => {
    if (!onlyIfTruncated || !triggerRef.current) return;
    const el = triggerRef.current;
    const update = () => setIsContentTruncated(isTruncated(el));
    update();

    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [onlyIfTruncated, children]);

  // MUI never shows a tooltip for an empty/false `title` — the simplest
  // way to express both "disabled" and "not truncated yet" without extra
  // `disableHoverListener`/`disableFocusListener`/`disableTouchListener`
  // plumbing.
  const shouldShow = !disabled && (!onlyIfTruncated || isContentTruncated);

  return (
    <MuiTooltip
      title={
        shouldShow ? (
          <TooltipErrorBoundary>
            <div
              className={styles.scrollContainer}
              style={{
                maxHeight: typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight,
              }}
            >
              {content}
            </div>
          </TooltipErrorBoundary>
        ) : (
          ""
        )
      }
      placement={placement}
      arrow={arrow}
      disableInteractive={!interactive}
      enterDelay={150}
      slotProps={{
        tooltip: {
          className: combineClasses(styles.bubble, styles[theme], className),
          style: { maxWidth },
        },
        arrow: { className: styles[`${theme}Arrow`] },
      }}
    >
      {
        // `children`'s own prop type is unknown to us (an arbitrary caller
        // element), so TS can't verify it accepts a `ref` — it always does
        // for a real DOM element, which is what this component requires
        // callers to pass (see the `children` doc comment above).
        cloneElement(children as ReactElement<{ ref?: typeof triggerRef }>, { ref: triggerRef })
      }
    </MuiTooltip>
  );
};

export default Tooltip;
